import React from "react";
import { render, unmountComponentAtNode } from "react-dom";
import Tippy from "@tippyjs/react/headless";
import parse from "html-react-parser";
import { twClassNames } from "../helpers/twClassNames";

//main react component for use in jsx and hbs files
//
//props:
//   button: button text for hovering (optional)
//   title: title of the tooltip inside the popup
//   placement: tippy positioning of popup (see below)
//   body: body of tooltip
//   buttonClasses: adds classes to the button for styling purposes

//example .hbs usage
//
// const { mountTooltip } = require("../../react/components/Tooltip.jsx");
// let tooltipTeardown = mountTooltip("#yourDomIDHere");
// window.reactUnmountables.push(tooltipTeardown);
// then setup your html with the id of the dom container

//example.jsx usage
//
// import { TooltipContainer } from "./Tooltip.jsx";
// add <TooltipContainer></TooltipContainer> to your jsx file

//Unmounting
// In .jsx files, this component will clean up after itself if the parent is unmounted.
// In .hbs files you'll need to setup unmounting logic.
// The mount function returns an unmount function you can use to:
//     1. manually unmount whenever you need
//     2. register with the global reactUnmountables array
//          example: window.reactUnmountables.push([your-returned-unmount-function]);

// In situations where the page is rerendering multiple times, The component may
// mount and unmount repeatedly. Since Tippy destroys the initial
// container contents during mount (why tho?), we'll track that here between renders.
let content = {};
const COLORS = {
    error: {
        text: "text-destructive",
        hoverText: "hover:text-destructive-hover",
        hoverBorder: "hover:border-transparent",
        hoverBg: "hover:bg-pink-50",
        titleText: "text-destructive",
        bodyText: "text-gray-700",
        containerBg: "border border-gray-200 bg-white"
    },
    primary: {
        text: "text-brand",
        hoverText: "hover:text-brand-hover",
        hoverBorder: "hover:border-transparent",
        hoverBg: "hover:bg-tint-hover",
        titleText: "text-brand",
        bodyText: "text-gray-700",
        containerBg: "border border-gray-200 bg-white"
    },
    secondary: {
        text: "text-gray-400",
        hoverText: "hover:text-gray-500",
        hoverBorder: "hover:border-transparent",
        hoverBg: "hover:bg-gray-100",
        titleText: "text-gray-900",
        bodyText: "text-gray-700",
        containerBg: "border border-gray-900 border-opacity-10 bg-white"
    },
    dark: {
        text: "text-gray-400",
        hoverText: "hover:text-gray-300",
        hoverBorder: "hover:border-transparent",
        hoverBg: "hover:bg-gray-400",
        titleText: "text-white",
        bodyText: "text-gray-200",
        containerBg: "border border-white border-opacity-10 bg-gray-900"
    },
    yellow: {
        text: "text-yellow-700",
        hoverText: "hover:text-yellow-600",
        hoverBorder: "hover:border-transparent",
        hoverBg: "hover:bg-yellow-100",
        titleText: "text-gray-900",
        bodyText: "text-gray-700",
        containerBg: "border border-gray-900 border-opacity-10 bg-white"
    }
};

export const TooltipContainer = (props) => {
    // for tippy placement options
    // https://atomiks.github.io/tippyjs/#placements
    const placement = props.placement || "top-start";

    let hasTitle = false;
    let colors = props.type ? COLORS[props.type] : COLORS.secondary;
    let titleIcon = props.titleIcon;

    if (props.title) {
        hasTitle = true;
    }

    let button;
    //sometimes we just want the ? icon instead of button text
    //we'll modify the style in that case to look correct
    if (props.customButton) {
        button = props.customButton;
    } else if (props.button || props.rightButton) {
        button = (
            <div
                className={twClassNames(
                    props.buttonClasses,
                    props.isClickable ? "cursor-pointer" : "cursor-help",
                    `relative flex h-5 w-auto items-center gap-1 rounded-lg border border-transparent bg-none px-1 text-[11px] font-medium ${colors.text} transition duration-200 ${colors.hoverBorder} ${colors.hoverBg}`
                )}
            >
                {props.rightButton && props.rightButton}
                <i className="far fa-question-circle text-xs"></i>
                {props.button && props.button}
            </div>
        );
    } else {
        button = (
            <div
                className={twClassNames(
                    props.buttonClasses,
                    props.isClickable ? "cursor-pointer" : "cursor-help",
                    `relative flex h-5 w-5 items-center justify-center rounded-full transition duration-200 ${colors.text} ${colors.hoverText} ${colors.hoverBg}`
                )}
            >
                <i
                    className={twClassNames(props.iconClasses, "far fa-question-circle text-xs")}
                ></i>
            </div>
        );
    }

    const body = typeof props.body === "string" ? parse(props.body) : props.body;

    if (props.type === "titleOnly") {
        return (
            <Tippy
                interactive={props.interactive !== undefined ? props.interactive : true}
                placement={props.placement || "top"}
                appendTo={document.body}
                delay={props.delay}
                disabled={props.disabled}
                offset={props.offset}
                render={(attrs) => (
                    <div {...attrs} className="titleOnlyTooltip -mt-1">
                        {props.title}
                        {titleIcon && <i className={titleIcon}></i>}
                    </div>
                )}
            >
                {button}
            </Tippy>
        );
    } else {
        return (
            <Tippy
                interactive={props.interactive !== undefined ? props.interactive : true}
                placement={placement}
                delay={props.delay}
                disabled={props.disabled}
                // for dev only
                // showOnCreate={true}
                // trigger={"click"}
                trigger={props.triggerOnClick ? "click" : "mouseenter focus"}
                appendTo={document.body}
                render={(attrs) => (
                    <div
                        {...attrs}
                        className={twClassNames(
                            hasTitle ? "flex-col gap-1" : "flex-start flex-row gap-2",
                            colors.containerBg,
                            colors.bodyText,
                            "z-10 -mt-2 flex w-96 cursor-default rounded p-3 font-sans text-xs shadow-md",
                            props.isSmall ? "w-auto" : "",
                            props.contentClassName
                        )}
                    >
                        {props.title && (
                            <div
                                className={twClassNames(
                                    "flex h-5 flex-row items-center gap-2 font-bold uppercase",
                                    colors.titleText
                                )}
                            >
                                <i className={titleIcon ? titleIcon : "fas fa-question-circle"}></i>
                                {props.title}
                            </div>
                        )}
                        <div>{body}</div>
                    </div>
                )}
            >
                {button}
            </Tippy>
        );
    }
};

//mount and unmount functions for including in hbs files
export const mountSingleTooltip = (domContainer, selector) => {
    if (domContainer) {
        if (!content[selector]) {
            content[selector] = domContainer.innerHTML;
        }

        const title = domContainer.dataset.title;
        const button = domContainer.dataset.button;
        const placement = domContainer.dataset.placement;
        const body = content[selector] != "" ? content[selector] : domContainer.innerHTML;
        const buttonClasses = domContainer.dataset.buttonclasses;
        const wide = domContainer.dataset.wide == "true" ? true : false;

        render(
            <TooltipContainer
                placement={placement}
                title={title}
                button={button}
                body={body}
                buttonClasses={buttonClasses}
                wide={wide}
            />,
            domContainer
        );
        // for situations where we need to hide the element
        // before Tippy initializes the element
        domContainer.classList.remove("invisible");
        domContainer.classList.remove("hidden");

        //return unmount function
        // call this whenever you need to register an unmount
        // window.reactUnmountables.push([your-returned-unmount-function]);
        return () => {
            if (domContainer) {
                unmountComponentAtNode(domContainer);
            }
        };
    }
};
export const mountTooltip = (selector) => {
    const domContainer = document.querySelector(selector);
    return mountSingleTooltip(domContainer, selector);
};

export const mountTooltips = (selector) => {
    const domContainers = document.querySelectorAll(selector);

    for (const domContainer of domContainers) {
        mountSingleTooltip(domContainer, selector);
    }
};

// Utility function for manually unmounting.
// Mostly for debugging, you shouldn't need this usually
export const unmountTooltip = (selector) => {
    const domContainer = document.querySelector(selector);
    if (domContainer) {
        return unmountComponentAtNode(domContainer);
    } else {
    }
};
